import {
  PREMIO_RA_MEDIA_END_PROGRESS,
  PREMIO_RA_PROMPT_DELAY_IN_MS,
  isPremioRaVotingOpen,
} from '@constants/premioReclameAqui'
import { PREMIO_RA_2026_PROMPT_SEEN } from '@constants/storages'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { useCallback, useEffect, useRef, useState } from 'react'

interface IUsePremioReclameAquiPromptParams {
  /** Aula em consumo: trocar de aula descarta um convite que ficou pendente. */
  lessonId?: number | null
}

interface IHandleLessonCompletedOptions {
  /**
   * Aula cuja mídia tem linha do tempo (vídeo/áudio): o convite espera a
   * reprodução chegar ao fim em vez de entrar por cima do player — no app uma
   * aula de vídeo é dada como concluída aos 75%.
   */
  waitForMediaEnd?: boolean
}

/**
 * Decide se o convite de votação do Prêmio RA 2026 entra quando o aluno
 * conclui uma aula. Aparece uma única vez por aluno (marca gravada no
 * AsyncStorage — localStorage na web, mesma API no app) e só durante a janela
 * da votação.
 */
export function usePremioReclameAquiPrompt({
  lessonId,
}: IUsePremioReclameAquiPromptParams = {}) {
  const [isPromptOpen, setIsPromptOpen] = useState(false)
  // Só libera depois de confirmar no storage que o aluno ainda não viu.
  const isEligible = useRef(false)
  // Conclusão que ficou esperando a mídia terminar.
  const isWaitingMediaEnd = useRef(false)
  // Maior marco de progresso visto na aula atual: no web a aula é concluída
  // justamente no marco de 100%, que pode chegar antes da conclusão.
  const highestProgress = useRef(0)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!isPremioRaVotingOpen()) return

    let active = true
    AsyncStorage.getItem(PREMIO_RA_2026_PROMPT_SEEN)
      .then((seenAt) => {
        if (active) isEligible.current = seenAt === null
      })
      .catch(() => undefined)

    return () => {
      active = false
    }
  }, [])

  // Trocar de aula zera o progresso observado e descarta a espera: o convite é
  // sobre a aula que o aluno acabou de concluir. A elegibilidade continua de
  // pé, então a próxima conclusão tenta de novo.
  useEffect(() => {
    isWaitingMediaEnd.current = false
    highestProgress.current = 0
  }, [lessonId])

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  const openPrompt = useCallback(() => {
    // Consome a elegibilidade antes de abrir: concluir duas aulas em sequência
    // não deve enfileirar dois convites.
    isEligible.current = false
    isWaitingMediaEnd.current = false
    AsyncStorage.setItem(
      PREMIO_RA_2026_PROMPT_SEEN,
      new Date().toISOString(),
    ).catch(() => undefined)

    timer.current = setTimeout(() => {
      timer.current = null
      setIsPromptOpen(true)
    }, PREMIO_RA_PROMPT_DELAY_IN_MS)
  }, [])

  const handleLessonCompleted = useCallback(
    ({ waitForMediaEnd }: IHandleLessonCompletedOptions = {}) => {
      if (!isEligible.current) return

      if (
        waitForMediaEnd &&
        highestProgress.current < PREMIO_RA_MEDIA_END_PROGRESS
      ) {
        isWaitingMediaEnd.current = true
        return
      }

      openPrompt()
    },
    [openPrompt],
  )

  const handleMediaProgress = useCallback(
    (progress: number) => {
      highestProgress.current = Math.max(highestProgress.current, progress)

      if (!isEligible.current || !isWaitingMediaEnd.current) return
      if (progress < PREMIO_RA_MEDIA_END_PROGRESS) return

      openPrompt()
    },
    [openPrompt],
  )

  const handleClosePrompt = useCallback(() => setIsPromptOpen(false), [])

  return {
    isPromptOpen,
    handleLessonCompleted,
    handleMediaProgress,
    handleClosePrompt,
  }
}
